/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `B2BEventUsersList` for the Caintra Connect frontend.
 * @module pages/B2BEventUsersList
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/B2BEventUsersList.module.css";

import { ReactComponent as TitleIcon } from "../assets/icons/AttendancePage/TitleIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/BusinessEvents/ChevronLeftIcon.svg";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";
import ResultsDropdown from "../components/ResultsDropdown";
import PageSelector from "../components/PageSelector";
import B2BEventUsersTable from "../components/B2BEventUsersTable/index.jsx";
import VirtualLinePauseAppointmentModal from "../modals/VirtualLinePauseAppointmentModal/index.jsx";
import VirtualLineCancelAppointmentModal from "../modals/VirtualLineCancelAppointmentModal/index.jsx";
import ReviewAppointmentModal from "../modals/ReviewAppointmentModal/index.jsx";
import {
  cancelB2BEventAppointment,
  getB2BEventBuyerAppointmentsVirtualLine,
  getB2BEventBuyerVirtualLineForUser,
  pauseShiftB2BEventBuyerAppointments,
  reviewB2BEventAppointment,
} from "../logic/b2bEvents";
import {
  handleResultsLimitChange,
  handlePageChange,
  paginateAttendances,
} from "../logic/attendance/index.js";
import { getUser, getUserRoleData } from "../logic/user";

import { Backdrop, CircularProgress } from "@mui/material";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import useDeviceDetection from "../hooks/useDeviceDetection";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import {
  appointmentMatchesVirtualLineSlot,
  canAccessB2BEventUsersList,
  isExecutiveBuyerUser,
  virtualLineSlotQueryParams,
} from "../utils/b2bEventVirtualLineSchedules";
import { canManageB2BVirtualLineQueue } from "../utils/accessControl";
import { DEFAULT_RESULTS_LIMIT } from "../utils/resultsLimitOptions.js";

const getSearchBarWidth = (deviceType) => {
  switch (deviceType) {
    case "laptop":
      return "100%";
    case "tablet":
      return "100%";
    case "mediumMobile":
      return "90%";
    case "smallMobile":
      return "100%";
    default:
      return "100%";
  }
};

/** Lowercase and strip diacritics so e.g. "moises" matches "Moisés". */
const normalizeSearchText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/** Preset banner: always DD-MM-YYYY (not locale-dependent). */
const formatPresetBannerDateDdMmYyyy = (fecha) => {
  if (fecha == null || String(fecha).trim() === "") {
    return "—";
  }
  const d = dayjs(String(fecha).slice(0, 10));
  return d.isValid() ? d.format("DD-MM-YYYY") : String(fecha);
};

const filterVirtualLineRows = (rows, searchQuery) => {
  const q = normalizeSearchText((searchQuery || "").trim());
  if (!q) {
    return rows;
  }
  return rows.filter((row) => {
    const name = normalizeSearchText(row.nombreUsuarioRemitente);
    const email = normalizeSearchText(row.correoElectronicoUsuarioRemitente);
    const company = normalizeSearchText(
      row.nombreComercialEmpresaUsuarioRemitente,
    );
    return name.includes(q) || email.includes(q) || company.includes(q);
  });
};

/**
 * Refreshes virtual line rows after mutations; honors optional time-slot preset.
 *
 * @param {object} p
 * @param {boolean} p.isExecutiveBuyer
 * @param {import("react-router").NavigateFunction} p.navigate
 * @param {import("react").MutableRefObject<object|null|undefined>} p.virtualLinePresetRef
 * @param {import("react").Dispatch<import("react").SetStateAction<Array>>} p.setBuyerVirtualLineEvents
 * @param {import("react").Dispatch<import("react").SetStateAction<Array>>} p.setAppointments
 */
const refreshVirtualLineAppointments = async ({
  isExecutiveBuyer,
  navigate,
  virtualLinePresetRef,
  setBuyerVirtualLineEvents,
  setAppointments,
}) => {
  const preset = virtualLinePresetRef.current;
  if (isExecutiveBuyer) {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      return;
    }
    const { events } = await getB2BEventBuyerVirtualLineForUser(
      userId,
      navigate,
    );
    const list = Array.isArray(events) ? events : [];
    setBuyerVirtualLineEvents(list);
    if (preset?.eventId) {
      const eid = String(preset.eventId);
      const match = list.find(
        (ev) => String(ev.eventId ?? ev.event?._id) === eid,
      );
      const raw = match?.appointments || [];
      setAppointments(
        raw.filter((a) => appointmentMatchesVirtualLineSlot(a, preset)),
      );
    } else {
      setAppointments(list.length > 0 ? list[0].appointments || [] : []);
    }
    return;
  }
  if (preset?.eventId && preset?.buyerUserId) {
    const slotQ = virtualLineSlotQueryParams(preset);
    const list = await getB2BEventBuyerAppointmentsVirtualLine(
      preset.eventId,
      preset.buyerUserId,
      navigate,
      slotQ,
    );
    const raw = Array.isArray(list) ? list : [];
    setAppointments(
      slotQ
        ? raw.filter((a) => appointmentMatchesVirtualLineSlot(a, preset))
        : raw,
    );
  }
};

const I18N_B2B_EVENT_USERS_LIST_MISSING_CONTEXT =
  "pages.b2bEventUsersList.pauseModalMissingContext";
const I18N_B2B_EVENT_USERS_LIST_SESSION_ERROR =
  "pages.b2bEventUsersList.pauseModalSessionError";

const B2BEventUsersList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const virtualLinePresetRef = useRef(
    location.state?.virtualLinePreset ?? null,
  );
  const { deviceType } = useDeviceDetection();

  const [currentUser, setCurrentUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [accessChecked, setAccessChecked] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [currentPage, setCurrentPage] = useState(1);
  const [buyerVirtualLineEvents, setBuyerVirtualLineEvents] = useState([]);
  const [executiveBuyerLineFetched, setExecutiveBuyerLineFetched] =
    useState(false);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [pauseModalAppointment, setPauseModalAppointment] = useState(null);
  const [reviewAppointmentModalOpen, setReviewAppointmentModalOpen] =
    useState(false);
  const [reviewAppointmentAppointment, setReviewAppointmentAppointment] =
    useState(null);
  const [pauseSubmitting, setPauseSubmitting] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelModalAppointment, setCancelModalAppointment] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [reviewAppointmentSubmitting, setReviewAppointmentSubmitting] =
    useState(false);

  const isExecutiveBuyer = isExecutiveBuyerUser(currentUser);
  const virtualLinePreset = virtualLinePresetRef.current;

  const pauseVirtualLineContext = useMemo(() => {
    if (isExecutiveBuyer) {
      const buyerUserId = localStorage.getItem("userId");
      const presetEventId = virtualLinePresetRef.current?.eventId;
      if (presetEventId && buyerUserId) {
        return {
          eventId: String(presetEventId),
          buyerUserId: String(buyerUserId),
        };
      }
      if (buyerVirtualLineEvents.length > 0) {
        const ev = buyerVirtualLineEvents[0];
        const eventId = ev.eventId ?? ev.event?._id;
        if (eventId && buyerUserId) {
          return {
            eventId: String(eventId),
            buyerUserId: String(buyerUserId),
          };
        }
      }
      return null;
    }
    const preset = virtualLinePresetRef.current;
    if (preset?.eventId && preset?.buyerUserId) {
      return {
        eventId: String(preset.eventId),
        buyerUserId: String(preset.buyerUserId),
      };
    }
    return null;
  }, [isExecutiveBuyer, buyerVirtualLineEvents]);

  useEffect(() => {
    const run = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        navigate("/");
        return;
      }
      const [userData, permissions] = await Promise.all([
        getUser(userId, navigate),
        getUserRoleData(userId, navigate),
      ]);
      setCurrentUser(userData);
      setPermissions(permissions);
      if (!canAccessB2BEventUsersList(userData, permissions)) {
        navigate("/home", { replace: true });
        return;
      }

      const preset = virtualLinePresetRef.current;

      if (!isExecutiveBuyerUser(userData)) {
        if (!preset?.eventId || !preset?.buyerUserId) {
          navigate("/b2bEvents", { replace: true });
          return;
        }
      }

      setAccessChecked(true);

      if (isExecutiveBuyerUser(userData)) {
        setIsLoading(true);
        try {
          const { events } = await getB2BEventBuyerVirtualLineForUser(
            userId,
            navigate,
          );
          const list = Array.isArray(events) ? events : [];
          setBuyerVirtualLineEvents(list);

          if (preset?.eventId) {
            const eid = String(preset.eventId);
            const match = list.find(
              (ev) => String(ev.eventId ?? ev.event?._id) === eid,
            );
            const raw = match?.appointments || [];
            setAppointments(
              raw.filter((a) => appointmentMatchesVirtualLineSlot(a, preset)),
            );
          } else if (list.length > 0) {
            setAppointments(list[0].appointments || []);
          } else {
            setAppointments([]);
          }
        } finally {
          setExecutiveBuyerLineFetched(true);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      try {
        const slotQ = virtualLineSlotQueryParams(preset);
        const rawList = await getB2BEventBuyerAppointmentsVirtualLine(
          preset.eventId,
          preset.buyerUserId,
          navigate,
          slotQ,
        );
        const raw = Array.isArray(rawList) ? rawList : [];
        setAppointments(
          slotQ
            ? raw.filter((a) => appointmentMatchesVirtualLineSlot(a, preset))
            : raw,
        );
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [navigate]);

  const filteredRows = useMemo(
    () => filterVirtualLineRows(appointments, searchQuery),
    [appointments, searchQuery],
  );

  const paginatedRows = useMemo(
    () => paginateAttendances(filteredRows, currentPage, resultsLimit),
    [filteredRows, currentPage, resultsLimit],
  );

  /** 1-based position in the full unfiltered virtual line, keyed by appointment `_id`. */
  const virtualLinePositionById = useMemo(() => {
    const m = new Map();
    for (let i = 0; i < appointments.length; i++) {
      const id = appointments[i]?._id;
      if (id != null) {
        m.set(String(id), i + 1);
      }
    }
    return m;
  }, [appointments]);

  const rowNumberOffset = (currentPage - 1) * resultsLimit;

  const showVirtualLineUi =
    isExecutiveBuyer && executiveBuyerLineFetched
      ? buyerVirtualLineEvents.length > 0 || Boolean(virtualLinePreset?.eventId)
      : Boolean(
          virtualLinePreset?.eventId &&
            virtualLinePreset?.buyerUserId &&
            accessChecked,
        );

  const showExecutiveEmptyState =
    isExecutiveBuyer &&
    executiveBuyerLineFetched &&
    buyerVirtualLineEvents.length === 0;

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePauseModalClose = useCallback(() => {
    setPauseModalOpen(false);
    setPauseModalAppointment(null);
  }, []);

  const handleReviewAppointmentModalClose = useCallback(() => {
    setReviewAppointmentModalOpen(false);
    setReviewAppointmentAppointment(null);
  }, []);

  const handlePauseClick = useCallback((row) => {
    setPauseModalAppointment(row);
    setPauseModalOpen(true);
  }, []);

  const handleCancelModalClose = useCallback(() => {
    setCancelModalOpen(false);
    setCancelModalAppointment(null);
  }, []);

  const handleCancelClick = useCallback((row) => {
    setCancelModalAppointment(row);
    setCancelModalOpen(true);
  }, []);

  /** @returns {Promise<{ error?: string }>} */
  const handleCancelAppointmentConfirm = useCallback(async () => {
    const ctx = pauseVirtualLineContext;
    const apt = cancelModalAppointment;
    if (!ctx?.eventId || !apt?._id) {
      return { error: t(I18N_B2B_EVENT_USERS_LIST_MISSING_CONTEXT) };
    }

    const idCita = String(apt._id);
    const idEventoB2B = String(ctx.eventId);
    const idUsuarioRemitente = apt.idUsuarioRemitente;
    const idUsuarioComprador = apt.idUsuarioComprador;
    const idUsuario =
      idUsuarioRemitente != null && String(idUsuarioRemitente).trim() !== ""
        ? String(idUsuarioRemitente)
        : idUsuarioComprador != null && String(idUsuarioComprador).trim() !== ""
          ? String(idUsuarioComprador)
          : ctx.buyerUserId
            ? String(ctx.buyerUserId)
            : null;

    if (!idUsuario) {
      return { error: t(I18N_B2B_EVENT_USERS_LIST_MISSING_CONTEXT) };
    }

    setCancelSubmitting(true);
    const result = await cancelB2BEventAppointment(
      idUsuario,
      idEventoB2B,
      idCita,
      navigate,
    );

    if (result.error) {
      setCancelSubmitting(false);
      return {
        error:
          result.error === "auth"
            ? t(I18N_B2B_EVENT_USERS_LIST_SESSION_ERROR)
            : result.error,
      };
    }

    try {
      await refreshVirtualLineAppointments({
        isExecutiveBuyer,
        navigate,
        virtualLinePresetRef,
        setBuyerVirtualLineEvents,
        setAppointments,
      });
    } catch (e) {
      logger.error("Error", { err: sanitizeError(e) });
    } finally {
      setCancelSubmitting(false);
    }

    handleCancelModalClose();
    return {};
  }, [
    pauseVirtualLineContext,
    cancelModalAppointment,
    navigate,
    t,
    isExecutiveBuyer,
    handleCancelModalClose,
  ]);
  const handleRatePartnerClick = useCallback((row) => {
    setReviewAppointmentAppointment(row);
    setReviewAppointmentModalOpen(true);
  }, []);

  const handleReviewAppointmentConfirm = useCallback(
    async (rating) => {
      setReviewAppointmentSubmitting(true);
      const result = await reviewB2BEventAppointment(
        reviewAppointmentAppointment,
        rating,
        navigate,
      );
      if (result.error) {
        setReviewAppointmentSubmitting(false);
        return { error: result.error };
      }

      const preset = virtualLinePresetRef.current;
      const slotQ =
        preset?.fecha &&
        preset?.eventId &&
        preset?.buyerUserId &&
        String(preset.eventId) ===
          String(reviewAppointmentAppointment.idEventoB2B) &&
        String(preset.buyerUserId) ===
          String(reviewAppointmentAppointment.idUsuarioComprador)
          ? virtualLineSlotQueryParams(preset)
          : null;
      const list = await getB2BEventBuyerAppointmentsVirtualLine(
        reviewAppointmentAppointment.idEventoB2B,
        reviewAppointmentAppointment.idUsuarioComprador,
        navigate,
        slotQ,
      );
      const raw = Array.isArray(list) ? list : [];
      setAppointments(
        slotQ
          ? raw.filter((a) => appointmentMatchesVirtualLineSlot(a, preset))
          : raw,
      );

      setReviewAppointmentSubmitting(false);
      handleReviewAppointmentModalClose();
      return { success: true };
    },
    [reviewAppointmentAppointment, navigate, handleReviewAppointmentModalClose],
  );

  const handlePauseConfirm = useCallback(
    async (pauseDuration) => {
      const ctx = pauseVirtualLineContext;
      const apt = pauseModalAppointment;
      if (!ctx || !apt?._id) {
        return { error: t(I18N_B2B_EVENT_USERS_LIST_MISSING_CONTEXT) };
      }

      setPauseSubmitting(true);
      const result = await pauseShiftB2BEventBuyerAppointments(
        ctx.eventId,
        ctx.buyerUserId,
        String(apt._id),
        pauseDuration,
        navigate,
      );

      if (result.error) {
        setPauseSubmitting(false);
        return {
          error:
            result.error === "auth"
              ? t(I18N_B2B_EVENT_USERS_LIST_SESSION_ERROR)
              : result.error,
        };
      }

      try {
        await refreshVirtualLineAppointments({
          isExecutiveBuyer,
          navigate,
          virtualLinePresetRef,
          setBuyerVirtualLineEvents,
          setAppointments,
        });
      } catch (e) {
        logger.error("Error", { err: sanitizeError(e) });
      } finally {
        setPauseSubmitting(false);
      }

      handlePauseModalClose();
      return {};
    },
    [
      pauseVirtualLineContext,
      pauseModalAppointment,
      navigate,
      t,
      isExecutiveBuyer,
      handlePauseModalClose,
    ],
  );

  const handleVirtualLinePresetGoBack = useCallback(() => {
    const preset = virtualLinePresetRef.current;
    if (preset?.returnPath) {
      navigate(preset.returnPath);
      return;
    }
    if (preset?.eventId && !isExecutiveBuyer) {
      navigate(`/b2bEvent/${preset.eventId}/virtual-line-companies`);
      return;
    }
    navigate("/b2bEvents");
  }, [navigate, isExecutiveBuyer]);

  const canManageQueue =
    canManageB2BVirtualLineQueue(currentUser, permissions) && !isExecutiveBuyer;
  const showLoadingOverlay = !accessChecked || isLoading;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>
        <div className={styles.headerInfoContainer}>
          {virtualLinePreset?.eventId ? (
            <div className={styles.headerInfo}>
              <button
                type="button"
                className={styles.returnButtonContainer}
                onClick={handleVirtualLinePresetGoBack}
              >
                <ChevronLeftIcon className={styles.returnButtonIcon} />
                <span className={styles.returnButtonText}>
                  {t("common.goBack")}
                </span>
              </button>
            </div>
          ) : null}
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>
              {t("pages.b2bEventUsersList.header")}
            </h1>
          </div>
        </div>
      </div>

      {virtualLinePreset?.eventId && (
        <div className={styles.presetBanner}>
          <p className={styles.presetBannerText}>
            {t("pages.b2bEventUsersList.presetSlotSummary", {
              event: virtualLinePreset?.eventNombre || "—",
              company: virtualLinePreset?.companyName || "—",
              buyer: virtualLinePreset?.buyerName || "—",
              date: formatPresetBannerDateDdMmYyyy(virtualLinePreset?.fecha),
              start: virtualLinePreset?.horaInicio || "—",
              end: virtualLinePreset?.horaFin || "—",
            })}
          </p>
        </div>
      )}

      {isExecutiveBuyer && buyerVirtualLineEvents.length > 0 && (
        <div className={styles.actionsContainer}>
          <span>
            {t("pages.b2bEventUsersList.executiveBuyerVirtualLineIntro")}
          </span>
        </div>
      )}

      {showVirtualLineUi && (
        <div className={styles.searchSection}>
          <SearchBar
            value={searchQuery}
            onChange={handleSearch}
            placeholder={t("pages.b2bEventUsersList.searchPlaceholder")}
            width={getSearchBarWidth(deviceType)}
          />
        </div>
      )}

      <div className={styles.mainList}>
        {showExecutiveEmptyState && (
          <div className={styles.noResults}>
            <p>{t("pages.b2bEventUsersList.noExecutiveBuyerEvents")}</p>
          </div>
        )}

        {showVirtualLineUi && paginatedRows.length === 0 && (
          <div className={styles.noResults}>
            <p>
              {appointments.length === 0
                ? t("pages.b2bEventUsersList.noAppointments")
                : t("pages.b2bEventUsersList.noSearchResults")}
            </p>
          </div>
        )}

        {showVirtualLineUi && paginatedRows.length > 0 && (
          <B2BEventUsersTable
            rows={paginatedRows}
            isColaborador={canManageQueue}
            rowNumberOffset={rowNumberOffset}
            virtualLinePositionById={virtualLinePositionById}
            onPauseClick={canManageQueue ? handlePauseClick : undefined}
            onCancelClick={canManageQueue ? handleCancelClick : undefined}
            onRatePartnerClick={handleRatePartnerClick}
          />
        )}
      </div>

      {showVirtualLineUi && filteredRows.length > 0 && (
        <div className={styles.footer}>
          <ResultsDropdown
            onResultsLimitChange={(opt) =>
              handleResultsLimitChange(opt, setResultsLimit, setCurrentPage)
            }
          />
          <PageSelector
            currentPage={currentPage}
            totalPages={Math.ceil(filteredRows.length / resultsLimit) || 1}
            onPageChange={(dir) =>
              handlePageChange(
                dir,
                currentPage,
                filteredRows,
                resultsLimit,
                setCurrentPage,
              )
            }
          />
        </div>
      )}

      <VirtualLinePauseAppointmentModal
        open={pauseModalOpen}
        onClose={handlePauseModalClose}
        appointment={pauseModalAppointment}
        onConfirmPause={canManageQueue ? handlePauseConfirm : undefined}
        isSubmitting={pauseSubmitting}
      />

      <VirtualLineCancelAppointmentModal
        open={cancelModalOpen}
        onClose={handleCancelModalClose}
        appointment={cancelModalAppointment}
        onConfirmCancel={
          canManageQueue ? handleCancelAppointmentConfirm : undefined
        }
        isSubmitting={cancelSubmitting}
      />
      <ReviewAppointmentModal
        open={reviewAppointmentModalOpen}
        onClose={handleReviewAppointmentModalClose}
        appointment={reviewAppointmentAppointment}
        onConfirmReview={handleReviewAppointmentConfirm}
        isSubmitting={reviewAppointmentSubmitting}
      />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 2,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={showLoadingOverlay}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#4eabe7" }} />
      </Backdrop>
    </div>
  );
};

export default B2BEventUsersList;
